import { apiClient } from '../apiClient';
import type { BillingAccount, BillingAccountUpdate, BillingStatus } from '../../types/billing';
import { CompanyService } from './companyService';

/** Aligned with Krysalis_Backend docs/STRIPE_PAYMENT_INTEGRATION_PLAN.md */
export type BillingProduct =
  | 'foundation_session'
  | 'bridge_session'
  | 'foundation_package'
  | 'krysalis_core'
  | 'co_development_early_adopter'
  | 'clarity'
  | 'growth'
  | 'franchise'
  | 'fitfinder_catalog'
  | 'investment_pilot';

export interface CreateCheckoutSessionBody {
  product: BillingProduct;
  company_id: string;
  /** ISO-8601; optional — backend defaults provisional scheduling */
  scheduled_at?: string;
  /**
   * SPA origin that started Checkout (`window.location.origin`).
   * Backend allowlists against CORS_ORIGINS / FRONTEND_BASE_URL for Stripe return URLs.
   */
  return_origin?: string;
  /** Customer-facing Stripe Promotion Code (optional discount). */
  promotion_code?: string;
  /**
   * Stripe Coupon id — only when allowlisted on the backend
   * (`STRIPE_ALLOWED_CHECKOUT_COUPON_IDS`). Prefer `promotion_code` for public entry.
   */
  coupon_id?: string;
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string;
  stripe_session_id: string;
}

export async function createCheckoutSession(
  body: CreateCheckoutSessionBody
): Promise<CreateCheckoutSessionResponse> {
  const res = await apiClient.post<CreateCheckoutSessionResponse>('/billing/checkout-session', body);
  return res.data;
}

/** POST /billing/deferred-subscription — partner_bundle / PARTNER_DEFERRED (no Stripe at create). */
export interface CreateDeferredSubscriptionBody {
  company_id: string;
  subscription_type_code: string;
}

export interface CreateDeferredSubscriptionResponse {
  subscription_id: string;
  company_id: string;
  subscription_type_code: string;
  status: string;
  arrangement: string;
}

export async function createDeferredSubscription(
  body: CreateDeferredSubscriptionBody
): Promise<CreateDeferredSubscriptionResponse> {
  const res = await apiClient.post<CreateDeferredSubscriptionResponse>(
    '/billing/deferred-subscription',
    body
  );
  return res.data;
}

/**
 * Company billing account helpers — delegates to {@link CompanyService} (`/crud/companies/{id}/billing`).
 * Kept as a named export for components that import `BillingService` separately from checkout.
 */
export const BillingService = {
  getByCompanyId: (companyId: string) => CompanyService.getBillingAccount(companyId),

  update: (companyId: string, data: BillingAccountUpdate) =>
    CompanyService.updateBillingAccount(companyId, data),

  async getStatus(companyId: string): Promise<BillingStatus> {
    const account = await CompanyService.getBillingAccount(companyId);
    return account?.status ?? 'No Billing Account';
  },
} as const;
